import type { SyntaxNode, Tree } from "@lezer/common";
import { rhaiWordLists } from "@/components/rhai-language";
import { RHAI_STDLIB_FUNCTIONS } from "./rhaiStdlib";

// The linter's offline scope pass (TTTL-225). Rhai resolves names at run
// time, so a parse alone accepts `let x = wer`; this walks the Lezer tree
// (rhai.grammar) with a lexical scope chain and reports what the engine
// would at run time, in its words. The rule, after rhaiscript/lsp's
// hir/errors.rs: a reference that is not a field access, has no binding,
// and is not `this`. A call's callee resolves the same way, against the
// script's functions and the known names too (a variable may hold a
// function pointer and be called directly).

export interface ScopeDiagnostic {
  from: number;
  to: number;
  message: string;
}

/** Names a script may call without defining: Rhai's standard packages,
 *  the tokenizer's builtin list, and mstream's registered helpers. */
export const RHAI_KNOWN_FUNCTIONS: ReadonlySet<string> = new Set([
  ...RHAI_STDLIB_FUNCTIONS,
  ...rhaiWordLists.builtinFunctions,
  ...rhaiWordLists.mstreamFunctions,
]);

class Scope {
  private readonly names = new Set<string>();

  constructor(private readonly parent: Scope | null) {}

  define(name: string): void {
    this.names.add(name);
  }

  has(name: string): boolean {
    return this.names.has(name) || (this.parent?.has(name) ?? false);
  }
}

interface Walk {
  text: string;
  scriptFunctions: Set<string>;
  known: ReadonlySet<string>;
  out: ScopeDiagnostic[];
}

const textOf = (w: Walk, n: SyntaxNode) => w.text.slice(n.from, n.to);

function children(n: SyntaxNode): SyntaxNode[] {
  const out: SyntaxNode[] = [];
  for (let c = n.firstChild; c; c = c.nextSibling) out.push(c);
  return out;
}

// A callee may also be one of the script's functions or a known name.
function isKnownCallee(w: Walk, name: string): boolean {
  return w.scriptFunctions.has(name) || w.known.has(name);
}

// Does the name resolve — as a value (`asCall` false) or as a callee?
function resolves(
  w: Walk,
  name: string,
  scope: Scope,
  asCall: boolean,
): boolean {
  if (name === "_" || scope.has(name)) return true;
  return asCall && isKnownCallee(w, name);
}

// A name used as a value (`asCall` false) or as a callee.
function reference(
  w: Walk,
  node: SyntaxNode,
  scope: Scope,
  asCall: boolean,
): void {
  const name = textOf(w, node);
  if (resolves(w, name, scope, asCall)) return;
  w.out.push({
    from: node.from,
    to: node.to,
    message: `${asCall ? "Function" : "Variable"} not found: ${name}`,
  });
}

// Does the subtree hold a parse error node?
function hasError(node: SyntaxNode): boolean {
  if (node.type.isError) return true;
  for (let c = node.firstChild; c; c = c.nextSibling)
    if (hasError(c)) return true;
  return false;
}

// Does the subtree hold a statement list of its own (a block)?
function hasBlock(node: SyntaxNode): boolean {
  if (node.name === "Block" || node.name === "Interpolation") return true;
  for (let c = node.firstChild; c; c = c.nextSibling)
    if (hasBlock(c)) return true;
  return false;
}

// A statement list (script, block, interpolation). The tree is error-
// tolerant, so a statement the parser could not place is skipped rather
// than checked against a mangled shape — but a broken declaration still
// binds its name (so later uses are not reported as undefined), and a
// statement with blocks of its own is walked so the intact statements
// inside get checked.
function visitStatements(w: Walk, nodes: SyntaxNode[], scope: Scope): void {
  for (const c of nodes) {
    if (c.type.isError) continue;
    if (hasError(c) && !hasBlock(c)) bindDeclaration(w, c, scope);
    else visit(w, c, scope);
  }
}

const DECLARATIONS = new Set(["LetDeclaration", "ConstDeclaration"]);

// A broken statement is not checked, but a declaration still binds.
function bindDeclaration(w: Walk, node: SyntaxNode, scope: Scope): void {
  if (!DECLARATIONS.has(node.name)) return;
  const def = node.getChild("VariableDefinition");
  if (def) scope.define(textOf(w, def));
}

function visitAll(w: Walk, nodes: SyntaxNode[], scope: Scope): void {
  for (const c of nodes) visit(w, c, scope);
}

// A Rhai function sees its parameters and nothing from outside — no
// top-level variables (those need `global::`), only other functions.
function visitFunction(w: Walk, node: SyntaxNode): void {
  const fnScope = new Scope(null);
  for (const c of children(node)) {
    if (c.name === "ParamList") {
      for (const p of c.getChildren("VariableDefinition"))
        fnScope.define(textOf(w, p));
    } else if (c.name === "Block") {
      visitStatements(w, children(c), fnScope);
    }
  }
}

// `let x = <init>`: the initializer sees the scope before x.
function visitDeclaration(w: Walk, node: SyntaxNode, scope: Scope): void {
  for (const c of children(node))
    if (c.name !== "VariableDefinition") visit(w, c, scope);
  const def = node.getChild("VariableDefinition");
  if (def) scope.define(textOf(w, def));
}

function visitClosure(w: Walk, node: SyntaxNode, scope: Scope): void {
  const inner = new Scope(scope);
  for (const c of children(node)) {
    if (c.name === "VariableDefinition") inner.define(textOf(w, c));
    else visit(w, c, inner);
  }
}

// The iterable is evaluated outside the loop; the body sees the bindings.
function visitFor(w: Walk, node: SyntaxNode, scope: Scope): void {
  const inner = new Scope(scope);
  for (const c of children(node)) {
    if (c.name === "VariableDefinition") inner.define(textOf(w, c));
    else if (c.name === "Block") visitStatements(w, children(c), inner);
    else visit(w, c, scope);
  }
}

function visitTry(w: Walk, node: SyntaxNode, scope: Scope): void {
  const [tryBlock, catchBlock] = node.getChildren("Block");
  if (tryBlock) visit(w, tryBlock, scope);
  const catchScope = new Scope(scope);
  const err = node.getChild("VariableDefinition");
  if (err) catchScope.define(textOf(w, err));
  if (catchBlock) visitStatements(w, children(catchBlock), catchScope);
}

function visitCall(w: Walk, node: SyntaxNode, scope: Scope): void {
  const callee = node.firstChild;
  if (callee?.name === "VariableName") reference(w, callee, scope, true);
  else if (callee) visit(w, callee, scope);
  const args = node.getChild("ArgList");
  if (args) visitAll(w, children(args), scope);
}

// Nodes that bind names or exempt them; anything else is walked through
// (member/index expressions, literals, operators, interpolations…: the
// object is a reference and gets checked, the property does not).
const VISITORS: Record<
  string,
  (w: Walk, node: SyntaxNode, scope: Scope) => void
> = {
  FunctionDeclaration: (w, node) => visitFunction(w, node),
  Block: (w, node, scope) =>
    visitStatements(w, children(node), new Scope(scope)),
  LetDeclaration: visitDeclaration,
  ConstDeclaration: visitDeclaration,
  Closure: visitClosure,
  ForExpression: visitFor,
  TryExpression: visitTry,
  CallExpression: visitCall,
  VariableName: (w, node, scope) => reference(w, node, scope, false),
  PropertyName: () => {},
  Path: () => {},
  ThisExpression: () => {},
  Interpolation: (w, node, scope) => visitStatements(w, children(node), scope),
};

function visit(w: Walk, node: SyntaxNode, scope: Scope): void {
  if (node.type.isError) return;
  const visitor = VISITORS[node.name];
  if (visitor) visitor(w, node, scope);
  else visitAll(w, children(node), scope);
}

/** The unresolved references of a parsed script, in document order.
 *  `knownFunctions` are the names callable without a definition. */
export function scopeDiagnostics(
  tree: Tree,
  text: string,
  knownFunctions: ReadonlySet<string>,
): ScopeDiagnostic[] {
  const w: Walk = {
    text,
    scriptFunctions: new Set(),
    known: knownFunctions,
    out: [],
  };
  const root = tree.topNode;
  for (const c of root.getChildren("FunctionDeclaration")) {
    const name = c.getChild("FunctionName");
    if (name) w.scriptFunctions.add(textOf(w, name));
  }
  visitStatements(w, children(root), new Scope(null));
  return w.out.sort((a, b) => a.from - b.from);
}
